/** Geocodifica una dirección chilena con Nominatim (OpenStreetMap) — es
 *  gratis y no necesita llave, así que no hay que pedirle al dueño que
 *  configure nada. Se usa poco (una vez por cliente, con el resultado
 *  guardado en su ficha), así que el límite de uso justo de Nominatim
 *  no es problema acá. */
export async function geocodificar(
  direccion: string,
  comuna?: string,
): Promise<{ lat: number; lng: number } | null> {
  const consulta = [direccion, comuna, 'Chile'].filter(Boolean).join(', ')
  const url = `https://nominatim.openstreetmap.org/search?format=json&limit=1&countrycodes=cl&q=${encodeURIComponent(consulta)}`
  try {
    const res = await fetch(url, { headers: { 'Accept-Language': 'es' } })
    if (!res.ok) return null
    const datos = await res.json()
    const primero = datos?.[0]
    if (!primero) return null
    const lat = Number(primero.lat)
    const lng = Number(primero.lon)
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
    return { lat, lng }
  } catch {
    return null
  }
}

const GOOGLE_PLACES_KEY = import.meta.env.VITE_GOOGLE_PLACES_API_KEY as string | undefined

export interface SugerenciaDireccion {
  /** Con Google, el id del lugar —hace falta un segundo llamado
   *  (`resolverSugerencia`) para conseguir sus coordenadas. Con el
   *  buscador de respaldo (OSM), un id armado a mano: ya viene con
   *  todo resuelto de una vez. */
  id: string
  fuente: 'google' | 'osm'
  /** Línea principal: la calle y el número. Lo que se muestra grande
   *  en la lista y lo que se guarda si se elige. */
  calle: string
  /** Línea secundaria: comuna, ciudad, lo que ayude a distinguir entre
   *  varias calles con el mismo nombre. */
  detalle: string
  /** Solo viene poblado de entrada para OSM. Para Google se completa
   *  recién al resolver la sugerencia elegida. */
  lat?: number
  lng?: number
  comuna?: string
}

/** Busca direcciones que calcen con lo que la persona va escribiendo,
 *  para el autocompletado del formulario de cliente. Usa el
 *  autocompletado de Google Places si hay una llave configurada (es el
 *  mismo buscador de Google Maps, así que conoce hasta direcciones sin
 *  numeración oficial); si no hay llave, cae solo a Nominatim
 *  (OpenStreetMap), que es gratis pero conoce bastante menos.
 *
 *  Recibe una señal para cancelar la búsqueda anterior si ya no
 *  importa —evita que una respuesta lenta y vieja le gane en la
 *  carrera a una más nueva y pise lo que la persona ya escribió
 *  después. */
export async function buscarDirecciones(
  texto: string,
  sesion: string,
  señal?: AbortSignal,
): Promise<SugerenciaDireccion[]> {
  return GOOGLE_PLACES_KEY
    ? buscarDireccionesGoogle(texto, sesion, señal)
    : buscarDireccionesOSM(texto, señal)
}

/** El segundo paso para una sugerencia de Google: sus coordenadas no
 *  vienen en la búsqueda (Google las cobra aparte, en la ficha del
 *  lugar), así que hay que pedirlas recién cuando la persona ya eligió
 *  una. Para OSM no hace falta red de nuevo —ya venían en la
 *  búsqueda—, así que solo se devuelve lo que ya se tenía. */
export async function resolverSugerencia(
  sugerencia: SugerenciaDireccion,
  sesion: string,
  señal?: AbortSignal,
): Promise<{ calle: string; comuna?: string; lat: number; lng: number } | null> {
  if (sugerencia.fuente === 'osm') {
    if (sugerencia.lat === undefined || sugerencia.lng === undefined) return null
    return { calle: sugerencia.calle, comuna: sugerencia.comuna, lat: sugerencia.lat, lng: sugerencia.lng }
  }
  return detallesLugarGoogle(sugerencia.id, sesion, señal)
}

async function buscarDireccionesGoogle(
  texto: string,
  sesion: string,
  señal?: AbortSignal,
): Promise<SugerenciaDireccion[]> {
  try {
    const res = await fetch('https://places.googleapis.com/v1/places:autocomplete', {
      method: 'POST',
      signal: señal,
      headers: { 'Content-Type': 'application/json', 'X-Goog-Api-Key': GOOGLE_PLACES_KEY! },
      body: JSON.stringify({
        input: texto,
        includedRegionCodes: ['cl'],
        languageCode: 'es',
        sessionToken: sesion,
      }),
    })
    if (!res.ok) return []
    const datos = await res.json()
    const sugerencias = datos.suggestions ?? []
    return sugerencias
      .map((s: GooglePrediccion): SugerenciaDireccion | null => {
        const p = s.placePrediction
        if (!p) return null
        return {
          id: p.placeId,
          fuente: 'google',
          calle: p.structuredFormat?.mainText?.text ?? p.text?.text ?? '',
          detalle: p.structuredFormat?.secondaryText?.text ?? '',
        }
      })
      .filter((s: SugerenciaDireccion | null): s is SugerenciaDireccion => s !== null)
  } catch {
    return []
  }
}

async function detallesLugarGoogle(placeId: string, sesion: string, señal?: AbortSignal) {
  try {
    const res = await fetch(
      `https://places.googleapis.com/v1/places/${placeId}?sessionToken=${sesion}`,
      {
        signal: señal,
        headers: {
          'X-Goog-Api-Key': GOOGLE_PLACES_KEY!,
          'X-Goog-FieldMask': 'location,addressComponents,formattedAddress',
        },
      },
    )
    if (!res.ok) return null
    const d = await res.json()
    const lat = d.location?.latitude
    const lng = d.location?.longitude
    if (typeof lat !== 'number' || typeof lng !== 'number') return null
    const comps: { longText?: string; types?: string[] }[] = d.addressComponents ?? []
    const porTipo = (t: string) => comps.find((c) => c.types?.includes(t))?.longText
    const calle = [porTipo('route'), porTipo('street_number')].filter(Boolean).join(' ') || d.formattedAddress
    const comuna = porTipo('locality') || porTipo('sublocality') || porTipo('administrative_area_level_3')
    return { calle, comuna, lat, lng }
  } catch {
    return null
  }
}

interface GooglePrediccion {
  placePrediction?: {
    placeId: string
    text?: { text: string }
    structuredFormat?: { mainText?: { text: string }; secondaryText?: { text: string } }
  }
}

async function buscarDireccionesOSM(texto: string, señal?: AbortSignal): Promise<SugerenciaDireccion[]> {
  const consulta = `${texto}, Chile`
  const url =
    `https://nominatim.openstreetmap.org/search?format=json&addressdetails=1&limit=5` +
    `&countrycodes=cl&q=${encodeURIComponent(consulta)}`
  try {
    const res = await fetch(url, { headers: { 'Accept-Language': 'es' }, signal: señal })
    if (!res.ok) return []
    const datos = await res.json()
    if (!Array.isArray(datos)) return []
    return datos
      .map((d, i): SugerenciaDireccion | null => {
        const lat = Number(d.lat)
        const lng = Number(d.lon)
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
        const a = d.address ?? {}
        const calle = [a.road, a.house_number].filter(Boolean).join(' ') || d.display_name
        const comuna = a.city_district || a.suburb || a.city || a.town || a.municipality || a.county
        return { id: `osm_${i}_${d.place_id}`, fuente: 'osm', calle, detalle: d.display_name, comuna, lat, lng }
      })
      .filter((s): s is SugerenciaDireccion => s !== null)
  } catch {
    return []
  }
}

/** Distancia en línea recta entre dos puntos, en kilómetros (fórmula de
 *  Haversine). No es la distancia real por calle, pero alcanza para
 *  ordenar una ruta de reparto de menor a mayor distancia. */
export function distanciaKm(
  a: { lat: number; lng: number },
  b: { lat: number; lng: number },
): number {
  const R = 6371
  const dLat = ((b.lat - a.lat) * Math.PI) / 180
  const dLng = ((b.lng - a.lng) * Math.PI) / 180
  const s =
    Math.sin(dLat / 2) ** 2 +
    Math.cos((a.lat * Math.PI) / 180) * Math.cos((b.lat * Math.PI) / 180) * Math.sin(dLng / 2) ** 2
  return R * 2 * Math.atan2(Math.sqrt(s), Math.sqrt(1 - s))
}

/** El próximo martes o jueves desde hoy (hoy mismo si cae justo ese
 *  día), en formato YYYY-MM-DD. */
export function proximoDia(diaSemana: 2 | 4): string {
  const hoy = new Date()
  const actual = hoy.getDay()
  let delta = diaSemana - actual
  if (delta < 0) delta += 7
  const d = new Date(hoy)
  d.setDate(hoy.getDate() + delta)
  return d.toISOString().slice(0, 10)
}

/** Ordena las paradas de una ruta que sale de la bodega y vuelve a la
 *  bodega, buscando el recorrido más corto.
 *
 *  El problema exacto (viajante de comercio) es imposible de resolver a
 *  la fuerza con más de un puñado de paradas, así que se usa la receta
 *  clásica: primero una ruta rápida yendo siempre a lo más cercano, y
 *  después se pule con 2-opt, que va desenredando los cruces —si dos
 *  tramos se cruzan, siempre es más corto descruzarlos—. Con las
 *  decenas de paradas de un día de reparto queda prácticamente óptima y
 *  corre al instante.
 *
 *  Devuelve los índices en el orden en que hay que visitarlos. */
export function ordenarRuta(
  bodega: { lat: number; lng: number },
  paradas: { lat: number; lng: number }[],
): number[] {
  const n = paradas.length
  if (n <= 2) return paradas.map((_, i) => i)

  // Matriz de distancias: incluye la bodega en la posición 0 para no
  // tener que tratarla aparte en cada cuenta.
  const puntos = [bodega, ...paradas]
  const d: number[][] = puntos.map((a) => puntos.map((b) => distanciaKm(a, b)))

  // 1) Vecino más cercano: desde la bodega, saltar siempre a la parada
  //    sin visitar que quede más cerca.
  const visitado = new Array(n + 1).fill(false)
  visitado[0] = true
  const orden: number[] = []
  let actual = 0
  for (let paso = 0; paso < n; paso++) {
    let mejor = -1
    let mejorDist = Infinity
    for (let i = 1; i <= n; i++) {
      if (!visitado[i] && d[actual][i] < mejorDist) {
        mejorDist = d[actual][i]
        mejor = i
      }
    }
    visitado[mejor] = true
    orden.push(mejor)
    actual = mejor
  }

  // 2) 2-opt: dar vuelta tramos mientras eso acorte el total. El
  //    recorrido siempre empieza y termina en la bodega (índice 0).
  let mejoro = true
  let vueltas = 0
  while (mejoro && vueltas < 60) {
    mejoro = false
    vueltas++
    for (let i = 0; i < orden.length - 1; i++) {
      for (let k = i + 1; k < orden.length; k++) {
        const previo = i === 0 ? 0 : orden[i - 1]
        const siguiente = k === orden.length - 1 ? 0 : orden[k + 1]
        // Solo compara los dos tramos que cambian, no la ruta entera.
        const antes = d[previo][orden[i]] + d[orden[k]][siguiente]
        const despues = d[previo][orden[k]] + d[orden[i]][siguiente]
        if (despues < antes - 1e-9) {
          orden.splice(i, k - i + 1, ...orden.slice(i, k + 1).reverse())
          mejoro = true
        }
      }
    }
  }

  // Se devuelven los índices de las paradas originales (sin la bodega).
  return orden.map((i) => i - 1)
}

/** Kilómetros totales de una ruta que sale de la bodega, pasa por las
 *  paradas en ese orden y vuelve a la bodega. */
export function kmTotales(
  bodega: { lat: number; lng: number },
  paradas: { lat: number; lng: number }[],
): number {
  if (!paradas.length) return 0
  let total = distanciaKm(bodega, paradas[0])
  for (let i = 0; i < paradas.length - 1; i++) {
    total += distanciaKm(paradas[i], paradas[i + 1])
  }
  return total + distanciaKm(paradas[paradas.length - 1], bodega)
}

/** Tarifa "tipo Uber" de un despacho especial ("Primer despacho"): un
 *  cargo base más un valor por cada kilómetro en línea recta desde la
 *  bodega hasta el cliente. Solo aplica a los despachos que no caen en
 *  la ruta programada del martes o jueves —esos los hace el
 *  despachador como una salida propia, así que le corresponde un pago
 *  aparte. */
export function tarifaDespacho(km: number, base: number, porKm: number): number {
  return Math.round(base + km * porKm)
}

/** El siguiente día de reparto (martes o jueves) estrictamente después
 *  de la fecha dada. Se usa cuando un cliente no confirma: su pedido se
 *  corre al próximo reparto en vez de quedar colgado. */
export function siguienteDiaReparto(desdeISO?: string): {
  dia: 'martes' | 'jueves'
  fecha: string
} {
  const base = desdeISO ? new Date(`${desdeISO}T12:00:00`) : new Date()
  const cursor = new Date(base)
  for (let i = 1; i <= 8; i++) {
    cursor.setDate(base.getDate() + i)
    const d = cursor.getDay()
    if (d === 2) return { dia: 'martes', fecha: cursor.toISOString().slice(0, 10) }
    if (d === 4) return { dia: 'jueves', fecha: cursor.toISOString().slice(0, 10) }
  }
  return { dia: 'martes', fecha: proximoDia(2) }
}
